import React, { useEffect } from "react";
import {
  Drawer,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Radio,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  DRAWER_FORM_WIDTH,
  drawerFooter,
  drawerTitle,
} from "../../../components";

interface Store {
  id: string;
  name: string;
}

interface ChequeStatus {
  id: string;
  name: string;
  code: string;
}

interface ChequeFormDrawerProps {
  open: boolean;
  onClose: () => void;
  onSave: (values: any) => Promise<void>;
  editingCheque: any | null;
  stores: Store[];
  chequeStatuses: ChequeStatus[];
  loading?: boolean;
}

export const ChequeFormDrawer: React.FC<ChequeFormDrawerProps> = ({
  open,
  onClose,
  onSave,
  editingCheque,
  stores,
  chequeStatuses,
  loading,
}) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    if (editingCheque) {
      form.setFieldsValue({
        type: editingCheque.type,
        cheque_no: editingCheque.cheque_no,
        amount: editingCheque.amount,
        bank_name: editingCheque.bank_name,
        bank_branch: editingCheque.bank_branch,
        cheque_date: dayjs(editingCheque.cheque_date),
        payee: editingCheque.payee,
        store_id: editingCheque.store_id,
        note: editingCheque.note,
      });
    } else {
      form.setFieldsValue({
        type: "in",
        cheque_date: dayjs(),
        status_id: chequeStatuses.find((s) => s.code === "pending")?.id,
        store_id: stores[0]?.id,
      });
    }
  }, [open, editingCheque, chequeStatuses, stores, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      await onSave({
        ...values,
        cheque_date: values.cheque_date.format("YYYY-MM-DD"),
      });
    } catch {
      message.warning("Please fill all required fields");
    }
  };

  return (
    <Drawer
      title={drawerTitle(editingCheque ? "Edit Cheque" : "Register Cheque")}
      open={open}
      onClose={onClose}
      width={DRAWER_FORM_WIDTH}
      placement="right"
      footer={drawerFooter({
        actions: [
          { label: "Cancel", onClick: onClose },
          {
            label: editingCheque ? "Save Changes" : "Register",
            type: "primary",
            loading: loading,
            disabled: loading,
            onClick: handleSubmit,
          },
        ],
      })}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="type" label="Direction" rules={[{ required: true }]}>
          <Radio.Group buttonStyle="solid">
            <Radio.Button value="in">Inward (Received)</Radio.Button>
            <Radio.Button value="out">Outward (Issued)</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item
            name="cheque_no"
            label="Cheque Number"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="e.g. CHQ123456" />
          </Form.Item>

          <Form.Item
            name="amount"
            label="Amount (LKR)"
            rules={[{ required: true, message: "Required" }]}
          >
            <InputNumber
              className="w-full"
              placeholder="0.00"
              min={0.01}
              formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(v) => (v ? v.replace(/,*/g, "") : "") as any}
            />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item
            name="bank_name"
            label="Bank Name"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input placeholder="e.g. Commercial Bank" />
          </Form.Item>

          <Form.Item name="bank_branch" label="Branch">
            <Input placeholder="e.g. Kollupitiya" />
          </Form.Item>
        </div>

        <div className="grid grid-cols-2 gap-x-4">
          <Form.Item
            name="cheque_date"
            label="Cheque Date"
            rules={[{ required: true, message: "Required" }]}
          >
            <DatePicker className="w-full" format="YYYY-MM-DD" />
          </Form.Item>

          <Form.Item name="payee" label="Payee / Payer">
            <Input placeholder="e.g. John Doe" />
          </Form.Item>
        </div>

        <Form.Item
          name="store_id"
          label="Store"
          rules={[{ required: true, message: "Required" }]}
        >
          <Select placeholder="Select store">
            {stores.map((s) => (
              <Select.Option key={s.id} value={s.id}>
                {s.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {!editingCheque && (
          <Form.Item
            name="status_id"
            label="Initial Status"
            rules={[{ required: true }]}
          >
            <Select placeholder="Select status">
              {chequeStatuses.map((s) => (
                <Select.Option key={s.id} value={s.id}>
                  {s.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item name="note" label="Notes">
          <Input.TextArea placeholder="Additional details..." rows={3} />
        </Form.Item>
      </Form>
    </Drawer>
  );
};
